import { Injectable } from "@angular/core";

import { createLowlight } from "lowlight";
import { Markdown } from "tiptap-markdown-3";

import { Editor } from "@tiptap/core";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TextAlign from "@tiptap/extension-text-align";
import StarterKit from "@tiptap/starter-kit";

@Injectable({ providedIn: "root" })
export class MarkdownService {
  private readonly _tiptapEditor: Editor;

  constructor() {
    this._tiptapEditor = new Editor({
      extensions: [
        StarterKit.configure({
          codeBlock: false,
          link: {
            openOnClick: true,
            autolink: true
          }
        }),
        Image.configure({ inline: false, allowBase64: true }),
        Table.configure({ resizable: false }),
        TableRow,
        TableHeader,
        TableCell,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        CodeBlockLowlight.configure({ lowlight: createLowlight() }),
        Markdown.configure({
          html: true, // Allow HTML to be parsed
          tightLists: true, // No <p> inside <li> in markdown
          tightListClass: "tight", // Class for tight lists
          bulletListMarker: "-", // Or '*'
          linkify: true, // Create links from URLs
          breaks: false, // Add <br> on single newlines
          transformPastedText: false, // Allow to paste markdown
          transformCopiedText: false // Copied text is transformed to markdown
        })
      ]
    });
  }

  markdownToHtml(markdown: string | null | undefined, removeTrailingEmptyParagraph = false): string {
    if (!markdown) return "";

    try {
      this._tiptapEditor.commands.setContent(markdown);
      let html = this._tiptapEditor.getHTML();

      if (removeTrailingEmptyParagraph) {
        html = html.replace(/<p>\s*<\/p>\s*$/i, "");
      }

      return html;
    } catch {
      return markdown ?? "";
    }
  }

  htmlToMarkdown(html: string): string {
    try {
      this._tiptapEditor.commands.setContent(html);
      return (this._tiptapEditor.storage as any).markdown.getMarkdown();
    } catch (e) {
      // Fallback for complex HTML that might fail in Tiptap
      // This is a simplified fallback; a full Turndown instance could be used here if needed
      console.warn("Tiptap htmlToMarkdown failed, using basic fallback", e);
      const div = document.createElement("div");
      div.innerHTML = html;
      return div.innerText || "";
    }
  }
}
